import { useEditor } from '../store/useEditor'
import { canConfirm, proposedSummary, runLabel, runTypeLabel } from '../lib/proposals'
import type { RoofProposal } from '../types/proposal'
import { IconTrash } from './icons'

/**
 * THE RUN REVIEW, IN THE PANEL.
 *
 * This used to float over the photograph, bottom left, 360 px wide and up to
 * 500 tall - a third of the picture on a laptop, sitting exactly where the
 * left end of a front eave usually is. The end-to-end test could not drag the
 * circle it needed because the press landed on the card, and a contractor hits
 * the same wall on the run he most wants to adjust. So it lives in the panel,
 * which is a side rail on a desk and a bottom sheet on a phone, and the
 * photograph is left alone.
 *
 * The card never says "dismiss". Every question here has an answer that quotes
 * footage and an answer that does not, and until one is chosen the run is
 * worth nothing.
 *
 * IT WAS STYLED FOR A PANEL THAT NO LONGER EXISTS.
 *
 * Every color in here was written inline for a dark rail: a white border at
 * fourteen percent, body text at sixty-five percent opacity, a salmon delete.
 * The panel is light now, so all of that rendered as pale gray on pale beige -
 * a card you could not read at the exact moment you had selected something to
 * change it. Inline style also wins over any stylesheet, so nothing in
 * editor.css could have rescued it.
 *
 * So the styling is classes now and the colors come from the same tokens as
 * the rest of the panel. The rules are in editor.css under THE RUN CARD.
 */

export default function RunCard() {
  const id = useEditor((s) => s.selectedProposalId)
  const proposals = useEditor((s) => s.doc.proposals)
  const present = useEditor((s) => s.present)

  const removeRun = useEditor((s) => s.removeRun)
  const setSideReturn = useEditor((s) => s.setSideReturn)
  const setOcclusion = useEditor((s) => s.setOcclusion)

  if (present || !id) return null
  const index = proposals.findIndex((q) => q.id === id)
  const p = proposals[index]
  if (!p) return null

  const check = canConfirm(p)
  const confirmed = p.status === 'confirmed'

  return (
    <div className="rc">
      <div className="rc-head">
        <strong>{runLabel(p, index)}</strong>
        <span className={confirmed ? 'rc-state on' : 'rc-state'}>
          {confirmed ? 'Lit' : 'Not lit yet'}
        </span>
      </div>
      <div className="rc-kind">
        {runTypeLabel(p)}
        {confirmed ? '' : ` · ${originLine(p)}`}
      </div>

      {(['start', 'end'] as const).map((which) => {
        const e = p.ends[which]
        if (!e.sideReturnWarning) return null
        const side = which === 'start' ? 'left' : 'right'

        // Once the cut is made the question is answered: the points past the
        // corner are gone, so an Include button here could not do what it says.
        if (e.trimApplied) {
          return (
            <div key={which} className="rc-blk done">
              Trimmed at the front corner. The {side} end stops on the front of the house. To light
              the side as well, draw it as its own run.
            </div>
          )
        }

        return (
          <div key={which} className="rc-blk ask">
            <div>
              This {side} end looks like it carries on past the front corner, onto the side of the
              house.
            </div>
            <div className="rc-row">
              <button type="button" className={e.sideReturnDecision === 'trim' ? 'rc-chip on' : 'rc-chip'} onClick={() => setSideReturn(p.id, which, 'trim')}>
                Trim at front corner
              </button>
              <button type="button" className={e.sideReturnDecision === 'include' ? 'rc-chip on' : 'rc-chip'} onClick={() => setSideReturn(p.id, which, 'include')}>
                Include side return
              </button>
              <button type="button" className={e.sideReturnDecision === 'unresolved' ? 'rc-chip on' : 'rc-chip'} onClick={() => setSideReturn(p.id, which, 'unresolved')}>
                Leave unconfirmed
              </button>
            </div>
          </div>
        )
      })}

      {p.spans.map((sp, i) => {
        if (sp.visibility === 'clear') return null
        /*
         * A PALM IN FRONT OF A ROOF IS NOT A QUESTION.
         *
         * Foliage over an edge that is plainly visible on both sides is a
         * note, not a hold-up: the run stays one straight line and gets
         * counted. Only a stretch nothing in the photo can settle is asked
         * about, and that one blocks.
         */
        const asks = sp.visibility === 'cannot_determine' && sp.decision !== 'continues'
        return (
          <div key={i} className={asks ? 'rc-blk ask' : 'rc-blk note'}>
            <div>
              {asks
                ? `Nothing in the photo shows whether the edge carries on ${sp.obstruction ? `behind ${obstruction(sp.obstruction)}` : 'through here'}.`
                : `Part of this edge runs behind ${sp.obstruction ? obstruction(sp.obstruction) : 'something'}. It is counted as carrying straight on.`}
            </div>
            <div className="rc-row">
              <button type="button" className={sp.decision === 'continues' ? 'rc-chip on' : 'rc-chip'} onClick={() => setOcclusion(p.id, i, 'continues')}>
                It continues
              </button>
              <button type="button" className={sp.decision === 'exclude' ? 'rc-chip on' : 'rc-chip'} onClick={() => setOcclusion(p.id, i, 'exclude')}>
                That is a gap
              </button>
              {asks && (
                <button type="button" className={sp.decision === 'unresolved' ? 'rc-chip on' : 'rc-chip'} onClick={() => setOcclusion(p.id, i, 'unresolved')}>
                  Leave unresolved
                </button>
              )}
            </div>
          </div>
        )
      })}

      {!confirmed && !check.ok && (
        <ul className="rc-blockers">
          {check.blockers.map((b, i) => (
            <li key={i}>{b.message}</li>
          ))}
        </ul>
      )}

      <div className="rc-foot">
        {/*
          Destructive is the secondary shape with red text and a red icon, and
          it is never a filled red button. RULES.md, and it was a full width
          block in salmon before, which is the loudest thing in a panel whose
          job is to change how a run looks.
        */}
        <button type="button" className="rc-remove" onClick={() => removeRun(p.id)}>
          <IconTrash size={15} />
          <span>Remove this run</span>
        </button>
      </div>

      <div className="rc-hint">
        {confirmed
          ? 'Drag a circle to move that end, or drag the line to move the whole run. The bulbs follow.'
          : 'This run is not lit yet. Answer the question above and it lights up.'}
      </div>
    </div>
  )
}

const obstruction = (k: NonNullable<RoofProposal['spans'][number]['obstruction']>) =>
  ({
    leaves: 'leaves',
    branch: 'a branch',
    trunk: 'a trunk',
    shadow: 'shadow',
    object: 'something in the way',
    glare: 'glare',
    blur: 'blur',
    other: 'something',
  })[k]

const originLine = (p: RoofProposal) => {
  switch (p.origin) {
    case 'manual':
      return 'you drew this'
    case 'ai':
      return `suggested by ${p.provider ?? 'a model'}`
    case 'classical':
      return 'traced from the edges in the photo'
    case 'recall':
      return 'restored from a run you approved before'
    default:
      return 'from an older save, provenance unrecorded'
  }
}
